// Google Analytics (GA4) loader.
//
// Gated on VITE_GA_MEASUREMENT_ID being present at build time — the same
// convention the OAuth providers use (an unset ID simply disables the feature).
// When the var is blank (local dev, tests, or any build that doesn't set it),
// initAnalytics() is a no-op and no tracking script is loaded.
//
// CONSENT: GA is opt-in. main.tsx no longer calls initAnalytics() on load —
// instead <CookieConsent> calls it only after the user grants consent (and on
// later loads if consent was previously granted). revokeAnalytics() disables
// collection and clears GA cookies when the user declines or withdraws.
//
// GA is injected from this bundled module (which is served from 'self') rather
// than as an inline <script> in index.html on purpose: the production CSP has
// no 'unsafe-inline' for scripts, so an inline gtag('config', …) block would be
// blocked. Loading it from our own bundle keeps it CSP-clean. The external
// gtag.js src + GA's beacon hosts are whitelisted in the CSP — see
// infrastructure/environments/prod/main.tf.

const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

// True only for builds that actually ship an analytics property. The consent
// banner uses this to decide whether to show at all — no GA, no banner.
export const analyticsConfigured = Boolean(MEASUREMENT_ID);

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

export function initAnalytics(): void {
  if (!MEASUREMENT_ID) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  // Guard against double-injection.
  if (window.dataLayer) return;

  // Clear any earlier opt-out flag in case the user previously declined.
  (window as unknown as Record<string, unknown>)[`ga-disable-${MEASUREMENT_ID}`] = false;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // GA's shim relies on pushing the live `arguments` object, not a copy.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', MEASUREMENT_ID);
}

// Withdraw analytics consent: set GA's documented opt-out flag so any loaded
// gtag stops sending hits, and best-effort clear the GA cookies it set. A full
// teardown of an already-loaded GA only completes on the next page load, which
// is acceptable for a withdrawal control.
export function revokeAnalytics(): void {
  if (!MEASUREMENT_ID) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  (window as unknown as Record<string, unknown>)[`ga-disable-${MEASUREMENT_ID}`] = true;

  const host = window.location.hostname;
  // Try both the host and a dot-prefixed parent domain, since GA may scope to either.
  const domains = ['', host, `.${host}`];
  document.cookie.split(';').forEach((entry) => {
    const name = entry.split('=')[0]?.trim();
    if (!name || !name.startsWith('_ga')) return;
    domains.forEach((domain) => {
      const domainPart = domain ? `; domain=${domain}` : '';
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainPart}`;
    });
  });
}
